/**
 * Authentication API client for the web app.
 *
 * Type-safe client for Better Auth endpoints at /api/auth/*.
 * Uses types from @animus/api-treaty for contract-first type safety.
 */

import { Result } from "true-myth/result";

import type { SessionData } from "@animus/api-treaty";
import type { AuthError } from "@animus/frontend-features/auth-store";

import { ENVIRONMENT } from "../environment/index.ts";

const AUTH_BASE = `${ENVIRONMENT.API_BASE_URL}/api/auth`;

function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
}

/**
 * Authentication API client with static methods.
 *
 * Calls Better Auth endpoints with type safety from @animus/api-treaty.
 */
export class AuthApi {
  static async getSession(): Promise<SessionData | null> {
    const response = await authFetch(`${AUTH_BASE}/get-session`);

    if (!response.ok) {
      return null;
    }

    return (await response.json()) as SessionData | null;
  }

  static async signIn(
    email: string,
    password: string
  ): Promise<Result<SessionData, AuthError>> {
    const response = await authFetch(`${AUTH_BASE}/sign-in/email`, {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      return Result.err({
        type: "api",
        code: data.code,
        message: data.message ?? "Sign in failed",
        canRetry: true,
      });
    }

    return Result.ok(data as SessionData);
  }

  static async signUp(
    email: string,
    password: string,
    name: string
  ): Promise<Result<SessionData, AuthError>> {
    const response = await authFetch(`${AUTH_BASE}/sign-up/email`, {
      method: "POST",
      body: JSON.stringify({ email, password, name }),
    });

    const data = await response.json();

    if (!response.ok) {
      return Result.err({
        type: "api",
        code: data.code,
        message: data.message ?? "Sign up failed",
        canRetry: true,
      });
    }

    return Result.ok(data as SessionData);
  }

  static async signOut(): Promise<{ success: boolean }> {
    const response = await authFetch(`${AUTH_BASE}/sign-out`, {
      method: "POST",
    });

    return { success: response.ok };
  }

  static async signInAnonymous(): Promise<Result<SessionData, AuthError>> {
    const response = await authFetch(`${AUTH_BASE}/sign-in/anonymous`, {
      method: "POST",
    });

    const data = await response.json();

    if (!response.ok) {
      return Result.err({
        type: "api",
        code: data.code,
        message: data.message ?? "Anonymous sign in failed",
        canRetry: true,
      });
    }

    return Result.ok(data as SessionData);
  }

  static async forgotPassword(
    email: string
  ): Promise<Result<{ success: boolean }, AuthError>> {
    const response = await authFetch(`${AUTH_BASE}/forgot-password`, {
      method: "POST",
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const data = await response.json();
      return Result.err({
        type: "api",
        code: data.code,
        message: data.message ?? "Forgot password request failed",
        canRetry: true,
      });
    }

    return Result.ok({ success: true });
  }

  static async resetPassword(
    token: string,
    newPassword: string
  ): Promise<Result<{ success: boolean }, AuthError>> {
    const response = await authFetch(`${AUTH_BASE}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ token, newPassword }),
    });

    if (!response.ok) {
      const data = await response.json();
      return Result.err({
        type: "api",
        code: data.code,
        message: data.message ?? "Password reset failed",
        canRetry: true,
      });
    }

    return Result.ok({ success: true });
  }
}
